import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  $createTextNode,
  $getRoot,
  $getSelection,
  $isElementNode,
  $isRangeSelection,
  COMMAND_PRIORITY_EDITOR,
  COMMAND_PRIORITY_HIGH,
  COMMAND_PRIORITY_NORMAL,
  createCommand,
  ElementNode,
  KEY_BACKSPACE_COMMAND,
  KEY_DELETE_COMMAND,
  LexicalCommand,
  LexicalNode,
  TextNode,
} from "lexical";
import { useCallback, useEffect } from "react";
import {
  $createPlaceholderNode,
  $isPlaceholderNode,
  PlaceholderNode,
  PlaceholderPayload,
} from "./PlaceholderNode";

export const INSERT_PLACEHOLDER_COMMAND: LexicalCommand<PlaceholderPayload> =
  createCommand();

export const FOCUS_FIRST_PLACEHOLDER_COMMAND: LexicalCommand<void> =
  createCommand();

type PlaceholderPluginProps = {
  enabled?: boolean;
  mode?: "add" | "edit";
};

export function PlaceholderPlugin({
  enabled,
  mode = "add",
}: PlaceholderPluginProps): null {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    if (!editor.hasNodes([PlaceholderNode])) {
      throw new Error("PlaceholderNode not registered on editor");
    }
  }, [editor]);

  useEffect(() => {
    return editor.registerCommand(
      FOCUS_FIRST_PLACEHOLDER_COMMAND,
      () => {
        setTimeout(() => {
          editor.update(
            () => {
              const root = $getRoot();
              const allNodes = $getAllNodes(root);
              const placeholderNodes = allNodes.filter(
                (node) => node instanceof PlaceholderNode,
              );

              if (placeholderNodes.length > 0) {
                const firstPlaceholderNode = placeholderNodes[0];
                firstPlaceholderNode.focus();
              }
            },
            {
              discrete: true,
            },
          );
        }, 0);

        return true;
      },
      COMMAND_PRIORITY_NORMAL,
    );
  }, [editor]);

  useEffect(() => {
    return editor.registerCommand(
      KEY_DELETE_COMMAND,
      () => {
        const selection = $getSelection();

        if ($isRangeSelection(selection)) {
          const [node] = selection.getNodes();

          if ($isPlaceholderNode(node)) {
            node.remove();
          }
        }
        return false;
      },
      COMMAND_PRIORITY_HIGH,
    );
  }, [editor]);

  useEffect(() => {
    return editor.registerCommand(
      KEY_BACKSPACE_COMMAND,
      () => {
        const selection = $getSelection();

        if ($isRangeSelection(selection)) {
          const [node] = selection.getNodes();

          if ($isPlaceholderNode(node)) {
            node.remove();
          }
        }
        return false;
      },
      COMMAND_PRIORITY_HIGH,
    );
  }, [editor]);

  const textTransform = useCallback(
    (textNode: TextNode) => {
      const textContent = textNode.getTextContent();

      // Regex to match {placeholder:...:...} format
      // Example: {placeholder:email:user@example.com}
      const match = /\{placeholder:([^}]*):([^}]*)\}/i.exec(textContent);

      if (!match) return;

      // Extract placeholder and value from the match
      const [, placeholder, value] = match;

      // Create a PlaceholderNode with the extracted values
      const node = $createPlaceholderNode({ placeholder, value, mode });

      // If the full text content matches, replace the entire textNode
      if (match[0] === textContent) {
        textNode.replace(node);
        return;
      }

      // Otherwise, split the text node based on the match index
      const startIndex = match.index || 0;
      const endIndex = startIndex + match[0].length;

      if (startIndex === 0) {
        // If the match is at the start, split at the end index and replace the first node
        const [currentNode] = textNode.splitText(endIndex);
        currentNode?.replace(node);
      } else {
        // If the match is in the middle, split at start and end, then replace the middle node
        const [, currentNode] = textNode.splitText(startIndex, endIndex);
        currentNode?.replace(node);
      }
    },
    [mode],
  );

  useEffect(() => {
    if (!enabled) return;
    return editor.registerNodeTransform(TextNode, textTransform);
  }, [editor, enabled, textTransform]);

  useEffect(() => {
    if (!enabled) return;
    const insertPlaceholderNode = (payload: PlaceholderPayload) => {
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          const node = $createPlaceholderNode(payload);
          selection.insertNodes([node, $createTextNode(" ")]);

          if (payload.focus) {
            setTimeout(() => {
              node.focus();
            }, 0);
          }
        }
      });
      return true;
    };
    return editor.registerCommand(
      INSERT_PLACEHOLDER_COMMAND,
      insertPlaceholderNode,
      COMMAND_PRIORITY_EDITOR,
    );
  }, [editor, enabled]);

  return null;
}

export function $getAllNodes(root: ElementNode): Array<LexicalNode> {
  const nodes = [];
  let child: LexicalNode | null = root.getFirstChild();
  while (child !== null) {
    nodes.push(child);
    if ($isElementNode(child)) {
      const subChildrenNodes = $getAllNodes(child);
      nodes.push(...subChildrenNodes);
    }
    child = child.getNextSibling();
  }
  return nodes;
}
